import * as React from 'react'
import { ChevronDown, ChevronUp, X } from 'lucide-react'

import { useDataTable } from '@/components/data-table/data-table-provider'
import { useSheetRowNavigation } from '@/hooks/useSheetRowNavigation'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import type { VisitRecord } from '@/features/visits/types'
import { formatVisitDate } from './VisitsColumns'
import { VisitsSheetSectionCard } from './VisitsSheetSectionCard'

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  const empty =
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim().length === 0)
  return (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="min-w-0 text-right break-words">
        {empty ? '-' : value}
      </span>
    </div>
  )
}

function MetaCard({
  title,
  rows,
}: {
  title: string
  rows: { label: string; value: React.ReactNode }[]
}) {
  return (
    <VisitsSheetSectionCard
      title={title}
      body={
        <div className="divide-y rounded-md border px-3">
          {rows.map((r) => (
            <MetaRow key={r.label} label={r.label} value={r.value} />
          ))}
        </div>
      }
    />
  )
}

function formatBool(value: boolean | null | undefined): string | null {
  if (value === null || value === undefined) return null
  return value ? 'Yes' : 'No'
}

/**
 * Visits details sheet — renders the selected row (from the DataTable context
 * via the `sheetSlot` prop) into titled section cards. All visit fields are
 * already on the row, so no detail fetch is needed.
 */
export function VisitsSheetWrapper() {
  const { table, rowSelection, isLoading } = useDataTable<
    VisitRecord,
    unknown
  >()

  const {
    selectedRowKey,
    selectedRow,
    prevId,
    nextId,
    onPrev,
    onNext,
    onOpenChange,
  } = useSheetRowNavigation(table, rowSelection, !!isLoading)

  const visit = selectedRow?.original
  const visitDate = visit ? formatVisitDate(visit.visit_date) : null

  return (
    <Sheet open={!!selectedRowKey} onOpenChange={onOpenChange}>
      <SheetContent
        onCloseAutoFocus={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="bg-neutral-ui-bg w-full max-w-none gap-0 overflow-y-auto rounded-l-xl p-0 sm:max-w-lg"
        showCloseButton={false}>
        <SheetHeader className="bg-neutral-ui-bg sticky top-0 z-10">
          <div className="flex items-start justify-between gap-2">
            <SheetTitle className="min-w-0 flex-1 truncate text-left text-lg font-semibold">
              {visit?.visit_type || 'Visit Details'}
            </SheetTitle>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              disabled={!prevId}
              onClick={onPrev}
              aria-label="Previous visit"
              title="Previous visit (↑)">
              <ChevronUp className="h-5 w-5" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              disabled={!nextId}
              onClick={onNext}
              aria-label="Next visit"
              title="Next visit (↓)">
              <ChevronDown className="h-5 w-5" />
            </Button>
            <SheetClose asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7">
                <X className="h-5 w-5" />
                <span className="sr-only">Close</span>
              </Button>
            </SheetClose>
          </div>
          {visitDate ? (
            <div className="text-neutral-low-contrast text-sm">{visitDate}</div>
          ) : null}
        </SheetHeader>

        <SheetDescription className="sr-only">
          Selected visit details
        </SheetDescription>

        <div className="px-4 pt-0 pb-4">
          {visit ? (
            <div className="space-y-4">
              <MetaCard
                title="Classification"
                rows={[
                  { label: 'Program', value: visit.program },
                  { label: 'Location', value: visit.location },
                  { label: 'Visit Groups', value: visit.visit_groups },
                  { label: 'Visit Sets', value: visit.visit_sets },
                ]}
              />
              <MetaCard
                title="Billing & Coding"
                rows={[
                  { label: 'Primary Payer', value: visit.primary_payer },
                  {
                    label: 'Primary Payer Group',
                    value: visit.primary_payer_group,
                  },
                  { label: 'Billable', value: formatBool(visit.is_billable) },
                  { label: 'Billing Codes', value: visit.billing_codes },
                  { label: 'Diagnosis Codes', value: visit.diagnosis_codes },
                ]}
              />
              <MetaCard
                title="UDS Flags"
                rows={[
                  { label: 'UDS Visit', value: formatBool(visit.is_uds_visit) },
                  {
                    label: 'UDS Medical Visit',
                    value: formatBool(visit.is_uds_medical_visit),
                  },
                ]}
              />
              <MetaCard
                title="Source / External System"
                rows={[
                  { label: 'Source Name', value: visit.source_name },
                  { label: 'Source Visit ID', value: visit.source_visit_id },
                  { label: 'Source Client ID', value: visit.source_patient_id },
                ]}
              />
            </div>
          ) : isLoading ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              Loading…
            </p>
          ) : (
            <p className="text-muted-foreground py-8 text-center text-sm">
              Select a row to view details
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
